import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Space,
  Typography,
  Tooltip,
} from "antd";
import { Match, MEDIA_TYPE } from "../../Lib/types";
import { FC } from "react";
import {
  CloseOutlined,
  PlusOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { TextField, useForm, useModal, useTable } from "@refinedev/antd";
import { BaseRecord } from "@refinedev/core";
import { errorNotification, successNotification } from "../../../../helpers";
import { MatchesMediaCard } from "./Matches-Media-Card";
import s from "./Matches-Media.module.scss";
import {
  AppImageUploader,
  EFileTypes,
  MassImageUploader,
} from "../../../../components";
import { v4 as randomId } from "uuid";
import {
  arrayLengthValidator,
  noSpecialCharValidator,
} from "../../../../validators";
import { extractMediaUrl, getVkVideoErrorMessage } from "../helpers/vk-video";

const { Title, Paragraph } = Typography;

const MATCH_MEDIA_INPUT_TYPE = {
  LINK_URL: "LINK_URL",
  LINK_IDS: "LINK_IDS",
  PHOTO: "PHOTO",
} as const;

type MatchMediaInputType =
  (typeof MATCH_MEDIA_INPUT_TYPE)[keyof typeof MATCH_MEDIA_INPUT_TYPE];

const matchMediaTypeSelect: { key: MatchMediaInputType; value: string }[] = [
  {
    key: MATCH_MEDIA_INPUT_TYPE.LINK_URL,
    value: "Ссылка",
  },
  {
    key: MATCH_MEDIA_INPUT_TYPE.LINK_IDS,
    value: " VK ID",
  },
  {
    key: MATCH_MEDIA_INPUT_TYPE.PHOTO,
    value: "Альбом изображений",
  },
];

const getMediaType = (type?: MatchMediaInputType): MEDIA_TYPE =>
  type === MATCH_MEDIA_INPUT_TYPE.PHOTO ? MEDIA_TYPE.PHOTO : MEDIA_TYPE.LINK;

const mediaLinkRule = {
  validator: async (_: unknown, value?: string) => {
    const mediaUrl = extractMediaUrl(value);

    if (!mediaUrl) return;

    try {
      new URL(mediaUrl);
    } catch {
      throw new Error("Укажите корректную ссылку");
    }
  },
};

type MatchesMediaProps = Pick<Match, "media"> & {
  onRefetchCard: () => void;
  matchId: number;
  leagueId: number;
  fnlId?: Match["fnlId"];
};

type MatchMediaFormValues = {
  type?: MatchMediaInputType;
  typeLink?: string;
  title?: string;
  url?: string;
  hashTags?: string[];
  photos?: { url: string }[];
  vidOid?: number | null;
  vidId?: number | null;
  videoUrl?: string | null;
};

const normalizeOptionalString = (value: unknown): string | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }

  const str = String(value).trim();
  return str ? str : undefined;
};

export const MatchesMedia: FC<MatchesMediaProps> = ({
  media,
  onRefetchCard,
  matchId,
  leagueId,
  fnlId,
}) => {
  const { modalProps, show, close } = useModal();

  const { formProps, form } = useForm<MatchMediaFormValues>({
    action: "create",
    resource: "matchesMedia",
    meta: {
      matchId,
      leagueId,
      fnlId,
      type: "LINK",
    },
    dataProviderName: "matchesMediaDataProvider",
    redirect: false,
    ...successNotification({
      successData: {
        message: "Медиа блок добавлен",
        type: "success",
      },
      callback: () => {
        close();
        onRefetchCard();
        form.resetFields();
      },
    }),
    ...errorNotification({}),
  });

  const { tableProps } = useTable<BaseRecord>({
    dataProviderName: "hashTagsDataProvider",
    resource: "hashtags",
    syncWithLocation: false,
    queryOptions: {
      enabled: true,
    },
  });

  const hashTags = (
    (tableProps.dataSource as unknown as readonly unknown[]) || []
  ).filter((value): value is string => typeof value === "string");

  const selectOptions = hashTags?.map((value) => {
    return { value, label: value };
  });

  const MAX_ALBUM_PHOTO_COUNT = 50;
  const watchType = Form.useWatch("type", formProps.form);
  const watchLength = Form.useWatch("photos", formProps.form);
  const stopAdding = (watchLength?.length ?? 0) < MAX_ALBUM_PHOTO_COUNT;
  const handleChangeType = () => {
    form.setFieldValue("url", null);
    form.setFieldValue("videoUrl", null);
    form.setFieldValue("vidOid", null);
    form.setFieldValue("vidId", null);
  };

  const afterClose = () => {
    form.resetFields();
  };

  const handleFinish = async (rawValues: unknown) => {
    const values = rawValues as MatchMediaFormValues;

    try {
      const mediaType = getMediaType(values.type);
      const { vidOid, vidId, url, ...rest } = values;
      const directVideoUrl =
        values.type === MATCH_MEDIA_INPUT_TYPE.LINK_URL
          ? extractMediaUrl(values.videoUrl)
          : undefined;

      return await formProps.onFinish?.({
        ...rest,
        type: mediaType,
        ...(values.type === MATCH_MEDIA_INPUT_TYPE.LINK_IDS
          ? { typeLink: "review" }
          : {}),
        url: normalizeOptionalString(url),
        photos:
          values.type === MATCH_MEDIA_INPUT_TYPE.PHOTO ? values.photos : [],
        ...(values.type === MATCH_MEDIA_INPUT_TYPE.LINK_IDS
          ? {
              vidOid:
                vidOid !== null && vidOid !== undefined
                  ? String(Math.abs(Number(vidOid)))
                  : undefined,
              vidId:
                vidId !== null && vidId !== undefined
                  ? String(Number(vidId))
                  : undefined,
              videoUrl: null,
            }
          : {}),
        ...(directVideoUrl
          ? { videoUrl: directVideoUrl, vidOid: null, vidId: null }
          : {}),
      });
    } catch (error) {
      message.error(
        getVkVideoErrorMessage(error, "Не удалось сохранить медиа")
      );
      return Promise.reject(error);
    }
  };

  const mediaItems =
    media?.filter(
      (content) =>
        content.type !== MEDIA_TYPE.ANNOUNCE_CONF &&
        content.type !== MEDIA_TYPE.ANNOUNCE_REVIEW
    ) ?? [];
  return (
    <>
      <Space direction={"vertical"} size={16} style={{ width: "100%" }}>
        <Space
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Title level={5}>Медиа</Title>
          {Boolean(media?.length) && (
            <Button onClick={show} icon={<PlusOutlined />}>
              Добавить медиа
            </Button>
          )}
        </Space>
        {!mediaItems.length ? (
          <Card
            style={{
              textAlign: "center",
              display: "flex",
              height: 200,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Space direction={"vertical"} size={16}>
              <Title level={5}>Медиа отсутствуют</Title>
              <Button onClick={show} icon={<PlusOutlined />}>
                Добавить медиа
              </Button>
            </Space>
          </Card>
        ) : (
          <Row gutter={[8, 8]}>
            {mediaItems?.map((content, index) => {
              return (
                <MatchesMediaCard
                  selectOptions={selectOptions}
                  key={content.id}
                  media={content}
                  count={index}
                  onRefetchCard={onRefetchCard}
                  fnlId={fnlId}
                />
              );
            })}
          </Row>
        )}
      </Space>

      <Modal
        title={
          <div>
            Добавление медиа{" "}
            <Tooltip
              title="Медиа автоматически добавляются в медиацентр. 
              В веб версии отображаются первое видео и фото первого альбома"
            >
              <InfoCircleOutlined />
            </Tooltip>
          </div>
        }
        onCancel={close}
        afterClose={afterClose}
        {...modalProps}
        footer={<></>}
      >
        <Form
          style={{
            width: "100%",
          }}
          {...formProps}
          onFinish={handleFinish}
          initialValues={{
            type: MATCH_MEDIA_INPUT_TYPE.LINK_URL,
            photos: [],
            url: null,
            videoUrl: null,
            vidOid: undefined,
            vidId: undefined,
          }}
          layout={"vertical"}
        >
          <Form.Item name={"type"} label={"Тип медиа"}>
            <Select
              onChange={handleChangeType}
              placeholder="Выберите тип медиа"
              notFoundContent={"Тип не найден"}
            >
              {matchMediaTypeSelect.map((translation) => {
                return (
                  <Select.Option key={translation.key} value={translation.key}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      <TextField value={translation.value} />
                    </div>
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>

          {watchType === MATCH_MEDIA_INPUT_TYPE.PHOTO && (
            <>
              <Form.Item
                label={
                  <div>
                    Обложка альбома {/*  */}
                    <Tooltip title="Максимальный размер фото 5Мб.">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </div>
                }
                name={"url"}
                rules={[
                  {
                    required: true,
                    message: "Укажите изображение",
                  },
                ]}
              >
                <AppImageUploader
                  type="MATCH"
                  entityId={`${matchId}/${randomId()}`}
                  serviceType={EFileTypes.MATCHES}
                  cropperProps={{
                    modalTitle: "Редактировать изображение",
                  }}
                  uploadProps={{
                    multiple: false,
                    children: "Прикрепите фото",
                    listType: "picture-card",
                  }}
                />
              </Form.Item>
              <Space size={8} direction="vertical" style={{ width: "100%" }}>
                <Paragraph className={s.newsBlockUpperHeader}>
                  <div>
                    <span className={s.requiredStar}>*</span> Фото альбома{" "}
                    {/*  */}
                    <Tooltip title="Максимальный размер фото 5Мб.">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </div>
                </Paragraph>

                <Form.List
                  name="photos"
                  rules={[
                    {
                      validator: async (_, names) => {
                        if (!names || names.length < 1) {
                          message.error("Должен быть хотя бы одно изображение");
                          return Promise.reject(
                            new Error("Должен быть хотя бы одно изображение")
                          );
                        }
                      },
                    },
                  ]}
                >
                  {(fields, { add, remove }) => (
                    <>
                      <div className={s.imageContainer}>
                        {fields.map((field) => (
                          <div key={field.key} className={s.appImageWrapper}>
                            <div
                              className={s.appImageDelete}
                              onClick={(event) => {
                                remove(field.name);
                                event.stopPropagation();
                              }}
                            >
                              <CloseOutlined
                                style={{
                                  color: "var(--danger)",
                                }}
                              />
                            </div>
                            <Form.Item
                              name={[field.name, "url"]}
                              rules={[
                                {
                                  required: true,
                                  message: (
                                    <span style={{ fontSize: "10px" }}>
                                      Укажите изображение
                                    </span>
                                  ),
                                },
                              ]}
                            >
                              <AppImageUploader
                                type="MATCH"
                                entityId={`${matchId}/${randomId()}`}
                                serviceType={EFileTypes.MATCHES}
                                cropperProps={{
                                  modalTitle: "Редактировать изображение",
                                }}
                                uploadProps={{
                                  multiple: false,
                                  children: "Прикрепите фото",
                                  listType: "picture-card",
                                }}
                              />
                            </Form.Item>
                          </div>
                        ))}
                        {stopAdding && (
                          <div
                            className={s.addImageContainer}
                            onClick={() => add()}
                          >
                            <PlusOutlined />
                            Добавить фото
                          </div>
                        )}
                      </div>
                      {stopAdding && (
                        <MassImageUploader
                          serviceType={EFileTypes.MATCHES}
                          type="MATCH"
                          entityId={`${matchId}`}
                          onUpload={(url) => add({ url })}
                          canUpload={() =>
                            (form.getFieldValue("photos")?.length ?? 0) <
                            MAX_ALBUM_PHOTO_COUNT
                          }
                        />
                      )}
                    </>
                  )}
                </Form.List>
              </Space>
            </>
          )}

          {(watchType === MATCH_MEDIA_INPUT_TYPE.LINK_URL ||
            watchType === MATCH_MEDIA_INPUT_TYPE.LINK_IDS) && (
            <>
              <Form.Item
                label={
                  <div>
                    Превью {/*  */}
                    <Tooltip title="Максимальный размер фото 5Мб.">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </div>
                }
                name={"url"}
                rules={[
                  {
                    required: true,
                    message: "Необходимо прикрепить превью",
                  },
                ]}
              >
                <AppImageUploader
                  type="MATCH"
                  entityId={`${matchId}/${randomId()}`}
                  serviceType={EFileTypes.MATCHES}
                  cropperProps={{
                    modalTitle: "Редактировать изображение",
                  }}
                  uploadProps={{
                    multiple: false,
                    children: "Прикрепите фото",
                    listType: "picture-card",
                  }}
                />
              </Form.Item>
            </>
          )}

          {watchType === MATCH_MEDIA_INPUT_TYPE.LINK_URL && (
            <Form.Item
              name={"videoUrl"}
              label={
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  Ссылка медиа{" "}
                  <Tooltip title="Можно вставить src из iframe или iframe целиком.">
                    <InfoCircleOutlined />
                  </Tooltip>
                </div>
              }
              rules={[
                {
                  required: true,
                  message: "Необходимо указать ссылку",
                },
                mediaLinkRule,
              ]}
            >
              <Input placeholder={"Укажите ссылку"}></Input>
            </Form.Item>
          )}

          {watchType === MATCH_MEDIA_INPUT_TYPE.LINK_IDS && (
            <>
              <Form.Item
                name={"vidOid"}
                label={
                  <div
                    style={{ display: "flex", gap: 4, alignItems: "center" }}
                  >
                    OID{" "}
                    <Tooltip title="OID из VK embed-ссылки">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </div>
                }
                rules={[
                  {
                    required: true,
                    message: "Укажите OID",
                  },
                ]}
              >
                <InputNumber
                  precision={0}
                  controls={false}
                  placeholder={"Например 29484355"}
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Form.Item
                name={"vidId"}
                label={"ID"}
                rules={[
                  {
                    required: true,
                    message: "Укажите ID",
                  },
                ]}
              >
                <InputNumber
                  min={1}
                  precision={0}
                  controls={false}
                  placeholder={"Например 456247241"}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </>
          )}

          <Form.Item
            name={"title"}
            label={
              <div>
                Заголовок{" "}
                <Tooltip
                  title="Не более 100 символов и не менее 5 символов. Поле не должно
              содержать специальных символов."
                >
                  <InfoCircleOutlined />
                </Tooltip>
              </div>
            }
            rules={[
              {
                required: true,
                message: "Необходимо указать заголовок",
              },
              {
                max: 100,
                min: 5,
                message: "Не более 100 символов и не менее 5 символов",
              },
              noSpecialCharValidator,
            ]}
          >
            <Input placeholder={"Укажите заголовок"}></Input>
          </Form.Item>
          <Form.Item
            name={"hashTags"}
            label={"Хэштеги"}
            rules={[arrayLengthValidator(3, "Хэштегов не может быть больше 3")]}
          >
            <Select
              placeholder={"Укажите теги"}
              showSearch
              mode={"multiple"}
              options={selectOptions}
              notFoundContent={
                <div
                  style={{
                    width: "100%",
                    height: 100,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  Укажите хэштеги
                </div>
              }
            />
          </Form.Item>
          <Button type={"primary"} htmlType={"submit"}>
            Добавить медиа
          </Button>
        </Form>
      </Modal>
    </>
  );
};
